import React, {useEffect, useState} from "react";
import {
  AbsoluteFill, useCurrentFrame, interpolate, spring,
  Audio, staticFile, Sequence,
  delayRender, continueRender, cancelRender,
} from "remotion";
import {z} from "zod";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {SceneBackground, type BgMode} from "../components/backgrounds/SceneBackground";
import {KineticText, type KineticMode} from "../components/text/KineticText";
import {GlowText} from "../components/dimus/GlowText";
import {AccentLine} from "../components/dimus/AccentLine";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import OdometerCounter from "../templates/vfx/data_odometer-counter_stat_1500ms";
import DonutProgressChart from "../templates/vfx/data_donut-chart_progress_1200ms";
import {ContentCaptionSync, type RemotionCaption} from "../components/dimus/ContentCaptionSync";
import {DarkBarChart, type BarDatum} from "../components/dimus/DarkBarChart";
import {SVGGrowthLine, type GrowthSeries} from "../components/dimus/SVGGrowthLine";
import {MetricCard} from "../components/dimus/MetricCard";

/**
 * ContentReel v2 — MOLDE PARAMETRIZÁVEL com Motion Design completo
 *
 * Usa a stack completa do servidor:
 *   SceneBackground (8 modos visuais por cena)
 *   KineticText (6 modos de animação — não mais texto estático)
 *   GlowText (números hero com pulsing glow)
 *   SPRINGS (heavy/snappy/bouncy) + FONT_SCALE + SAFE_ZONES
 *   VFX: odometer | donut | barchart | growthline | metriccard
 *   Dual-stack captions (Whisper + @remotion/captions)
 *
 * Layouts: HOOK | BODY | CONCEITO | LISTA | MISSAO | PAYOFF | CTA
 * bgMode: gradient-drift | floating-shapes | grid-dots | grid-lines |
 *         particles-dense | dark-void | warm-glow | split-tone
 */

// ── bgAnim → bgMode compat map ────────────────────────────────────────────────
const BGANIM_MAP: Record<string, BgMode> = {
  "particles": "particles-dense",
  "kinetic": "floating-shapes",
  "conic": "gradient-drift",
  "dataflow": "grid-dots",
  "none": "dark-void",
};

const SZ = SAFE_ZONES.instagram;
const COLORS: Record<string, string> = {
  yellow: IDV.third,
  green:  IDV.secondary,
  blue:   IDV.primary,
  red:    IDV.error,
};
const WHITE  = "#f5f5f7";
const MUTED  = "#a3a3a7";
const TRANSITION_DUR = 0; // hard-cut (SceneFade cuida do fade interno)

const col = (c: string) => COLORS[c] || c;

// ── Schema ───────────────────────────────────────────────────────────────────
export const sceneSchema = z.object({
  layout: z.enum(["HOOK","BODY","CONCEITO","LISTA","MISSAO","PAYOFF","CTA"]),
  kicker:    z.string(),
  titulo:    z.string(),
  subtitulo: z.string().optional(),
  bullets:   z.array(z.string()).optional(),
  card:      z.string().optional(),
  audio:     z.string(),
  accent:    z.string(),

  // Background — suporta bgMode novo OU bgAnim legado
  bgMode: z.enum([
    "gradient-drift","floating-shapes","grid-dots","grid-lines",
    "particles-dense","dark-void","warm-glow","split-tone",
  ] as const).optional(),
  bgAnim: z.enum(["none","dataflow","particles","kinetic","conic"]).default("none"),

  // Motion — controles por cena
  kineticMode: z.enum([
    "stagger-up","stagger-scale","stagger-blur","wave","split-reveal","bounce",
  ] as const).optional(),
  glowStat: z.string().optional(),       // número hero com glow pulsante (ex: "90%")
  highlightWords: z.array(z.string()).optional(), // palavras destaque no KineticText

  // VFX (odometer | donut | barchart | growthline | metriccard)
  vfx: z.object({
    type: z.enum(["odometer","donut","barchart","growthline","metriccard"]),
    value:      z.number().optional(),
    prefix:     z.string().optional(),
    suffix:     z.string().optional(),
    label:      z.string().optional(),
    sublabel:   z.string().optional(),
    bars:       z.array(z.object({
      value: z.string(), label: z.string(), heightPx: z.number(),
      color: z.string().optional(), glowColor: z.string().optional(),
    })).optional(),
    barSeparator: z.string().optional(),
    series:     z.array(z.object({
      color: z.string(), curveType: z.string().optional(), endLabel: z.string().optional(),
      fillColorTop: z.string().optional(), fillColorBottom: z.string().optional(),
    })).optional(),
    growthWidth:  z.number().optional(),
    growthHeight: z.number().optional(),
    variant:    z.enum(["formula","stat","comparison"]).optional(),
    tokens:     z.array(z.object({
      text: z.string(), type: z.enum(["operand","operator","result"]), color: z.string().optional(),
    })).optional(),
    badge:      z.object({ text: z.string(), bgColor: z.string(), textColor: z.string() }).optional(),
    compLeft:   z.object({ text: z.string(), color: z.string() }).optional(),
    compRight:  z.object({ text: z.string(), color: z.string() }).optional(),
    compSeparator: z.string().optional(),
  }).optional(),

  durFrames: z.number(),
});

export const contentReelSchema = z.object({
  scenes:    z.array(sceneSchema).min(1),
  bgm:       z.string().default("music/cafe-lofi.mp3"),
  bgmVolume: z.number().default(0.12),
  ttsVolume: z.number().default(0.95),
  captionsSource:   z.string().optional(),
  captionsPreset:   z.enum(["bold-center-pop","editorial-dimus","humane-statement","coolvetica-friendly"]).optional(),
  captionsPosition: z.enum(["center","above-subject","bottom-third"]).optional(),
  captionsHighlight: z.string().optional(),
  captionsEmphasis:  z.string().optional(),
});

// ── Resolve bgMode (novo campo OU legado bgAnim) ─────────────────────────────
function resolveBgMode(scene: z.infer<typeof sceneSchema>): BgMode {
  if (scene.bgMode) return scene.bgMode;
  return BGANIM_MAP[scene.bgAnim] ?? "dark-void";
}

// ── Resolve KineticMode por layout ───────────────────────────────────────────
function resolveKineticMode(scene: z.infer<typeof sceneSchema>): KineticMode {
  if (scene.kineticMode) return scene.kineticMode;
  if (scene.layout === "HOOK" || scene.layout === "CTA") return "stagger-blur";
  if (scene.layout === "LISTA") return "stagger-up";
  if (scene.layout === "MISSAO" || scene.layout === "PAYOFF") return "split-reveal";
  return "stagger-up";
}

// ── renderRich: **texto** vira span colorido ─────────────────────────────────
const renderRich = (text: string, accent: string) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <span key={i} style={{color: col(accent)}}>{p.slice(2,-2)}</span>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );

// ── SceneFade: fade-in 12f / fade-out 15f dentro de cada Sequence ───────────
const SceneFade: React.FC<{duration:number;children:React.ReactNode}> = ({duration,children}) => {
  const f = useCurrentFrame();
  const op = Math.min(
    interpolate(f, [0,12], [0,1], CLAMP),
    interpolate(f, [duration-15, duration], [1,0], CLAMP),
  );
  const lift = interpolate(f, [0,20], [12,0], CLAMP);
  return (
    <AbsoluteFill style={{opacity: op, transform: `translateY(${lift}px)`}}>
      {children}
    </AbsoluteFill>
  );
};

// ── VFX renderer ─────────────────────────────────────────────────────────────
const VfxBlock: React.FC<{vfx: NonNullable<z.infer<typeof sceneSchema>["vfx"]>; accent: string}> = ({vfx, accent}) => {
  const f = useCurrentFrame();
  const a = col(accent);
  const entryOp = interpolate(f, [40,80], [0,1], CLAMP);

  return (
    <div style={{marginTop: 48, display:"flex", justifyContent:"center", opacity: entryOp}}>
      {vfx.type==="odometer" && typeof vfx.value==="number" && (
        <OdometerCounter value={vfx.value} prefix={vfx.prefix||""} suffix={vfx.suffix||""} label={vfx.label||""} color={a}/>
      )}
      {vfx.type==="donut" && typeof vfx.value==="number" && (
        <DonutProgressChart value={vfx.value} sublabel={vfx.sublabel||""} color={a} dark={true}/>
      )}
      {vfx.type==="barchart" && vfx.bars && (
        <DarkBarChart bars={vfx.bars as BarDatum[]} separator={vfx.barSeparator?{symbol:vfx.barSeparator}:undefined}/>
      )}
      {vfx.type==="growthline" && vfx.series && (
        <SVGGrowthLine series={vfx.series as GrowthSeries[]} width={vfx.growthWidth??240} height={vfx.growthHeight??154}/>
      )}
      {vfx.type==="metriccard" && vfx.variant==="formula" && vfx.tokens && (
        <MetricCard variant="formula" tokens={vfx.tokens} badge={vfx.badge}/>
      )}
      {vfx.type==="metriccard" && vfx.variant==="stat" && vfx.label && (
        <MetricCard variant="stat" value={vfx.value?.toString()??""} label={vfx.label} sublabel={vfx.sublabel} badge={vfx.badge}/>
      )}
      {vfx.type==="metriccard" && vfx.variant==="comparison" && vfx.compLeft && vfx.compRight && (
        <MetricCard variant="comparison" left={vfx.compLeft} right={vfx.compRight} separator={vfx.compSeparator??"→"}/>
      )}
    </div>
  );
};

// ── SceneRenderer — agora com SceneBackground + KineticText + GlowText ───────
const SceneRenderer: React.FC<{s: z.infer<typeof sceneSchema>; ttsVolume: number}> = ({s, ttsVolume}) => {
  const f = useCurrentFrame();
  const a = col(s.accent);
  const isHook   = s.layout==="HOOK" || s.layout==="CTA";
  const isMissao = s.layout==="MISSAO" || s.layout==="PAYOFF";
  const kineticMode = resolveKineticMode(s);
  const bgMode = resolveBgMode(s);

  // Spring para o bloco de conteúdo
  const contentSp = spring({frame: f-8, fps:30, config: isHook ? SPRINGS.heavy : SPRINGS.snappy});

  // Strip ** from titulo para KineticText (que usa highlightWords separado)
  const tituloClean = s.titulo.replace(/\*\*/g, "");
  // Extrair palavras entre ** para highlight
  const autoHighlight = [...(s.titulo.matchAll(/\*\*([^*]+)\*\*/g))].map(m => m[1]);
  const highlightWords = [...(s.highlightWords ?? []), ...autoHighlight];

  const topOffset = SZ.top + (isHook ? 80 : 40);

  return (
    <AbsoluteFill>
      {/* Background rico: 8 modos visuais */}
      <SceneBackground
        mode={bgMode}
        accent={a}
        accent2={IDV.primary}
        base="#0a0a0a"
        grain
        vignette
        noise
        seed={s.layout}
      />

      {/* Áudio TTS da cena */}
      <Audio src={staticFile(s.audio)} volume={ttsVolume}/>

      {/* Bloco de conteúdo — posicionado pela safe zone */}
      <div style={{
        position:"absolute",
        top: topOffset,
        left: SZ.left,
        right: SZ.right,
        opacity: isHook ? contentSp : 1,
        transform: isHook ? `scale(${0.92+0.08*contentSp})` : undefined,
      }}>

        {/* GlowStat — número hero com glow pulsante (opcional) */}
        {s.glowStat && (
          <div style={{marginBottom: 24}}>
            <GlowText text={s.glowStat} delay={0} size={FONT_SCALE.hero.min} color={a}/>
          </div>
        )}

        {/* Kicker — label pequeno acima do título */}
        <div style={{
          fontFamily: FONT_STACK,
          color: isMissao ? a : MUTED,
          fontSize: FONT_SCALE.label.min,
          fontWeight: FONT_SCALE.label.weight,
          letterSpacing: 2.5,
          marginBottom: 16,
          opacity: interpolate(f, [4,18], [0,1], CLAMP),
          transform: `translateY(${interpolate(f, [4,18], [8,0], CLAMP)}px)`,
        }}>
          {s.kicker}
        </div>

        {/* AccentLine */}
        <AccentLine color={a} delay={isHook?20:6}/>

        {/* Título com KineticText — animação real, não texto estático */}
        <div style={{marginTop: 36}}>
          <KineticText
            text={tituloClean}
            mode={kineticMode}
            delay={isHook ? 24 : 12}
            fontSize={isHook ? FONT_SCALE.h1.max : FONT_SCALE.h1.min}
            fontWeight={FONT_SCALE.h1.weight}
            color={WHITE}
            highlightWords={highlightWords}
            highlightColor={a}
            textAlign="left"
            lineHeight={1.15}
            maxWidth="100%"
            gap={isHook ? 3 : 2}
          />
        </div>

        {/* Card destacado */}
        {s.card && (() => {
          const cardSp = spring({frame: f-60, fps:30, config: SPRINGS.snappy});
          return (
            <div style={{
              marginTop: 44,
              padding: "32px 36px",
              borderRadius: 18,
              background: `${a}12`,
              border: `1.5px solid ${a}40`,
              opacity: cardSp,
              transform: `translateY(${interpolate(cardSp, [0,1], [16,0], CLAMP)}px)`,
            }}>
              <div style={{
                fontFamily: FONT_STACK,
                color: WHITE,
                fontSize: FONT_SCALE.body.min,
                fontWeight: 600,
                lineHeight: 1.4,
              }}>
                {renderRich(s.card, s.accent)}
              </div>
            </div>
          );
        })()}

        {/* VFX (odometer, donut, barchart, growthline, metriccard) */}
        {s.vfx && <VfxBlock vfx={s.vfx} accent={s.accent}/>}

        {/* Bullets com stagger spring */}
        {s.bullets && (
          <div style={{marginTop: 48, display:"flex", flexDirection:"column", gap: 20}}>
            {s.bullets.map((b, i) => {
              const bSp = spring({frame: f-(20+i*18), fps:30, config: SPRINGS.snappy});
              return (
                <div key={i} style={{
                  display:"flex", alignItems:"flex-start", gap: 16,
                  opacity: bSp,
                  transform: `translateX(${interpolate(bSp, [0,1], [-20,0], CLAMP)}px)`,
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius:"50%",
                    background: a, marginTop: 10, flexShrink: 0,
                    boxShadow: `0 0 8px ${a}80`,
                  }}/>
                  <div style={{
                    fontFamily: FONT_STACK,
                    color: WHITE,
                    fontSize: FONT_SCALE.body.min,
                    fontWeight: FONT_SCALE.body.weight,
                    lineHeight: 1.35,
                  }}>
                    {renderRich(b, s.accent)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Subtítulo com KineticText (modo diferente do título) */}
        {s.subtitulo && (
          <div style={{marginTop: 40}}>
            <KineticText
              text={s.subtitulo.replace(/\*\*/g,"")}
              mode="wave"
              delay={70}
              fontSize={FONT_SCALE.h2.min}
              fontWeight={FONT_SCALE.h2.weight}
              color={a}
              textAlign="left"
              lineHeight={1.3}
              maxWidth="95%"
            />
          </div>
        )}
      </div>
    </AbsoluteFill>
  );
};

export const calcContentReelDuration = (scenes: {durFrames:number}[]) =>
  scenes.reduce((s,sc)=>s+sc.durFrames, 0) + (scenes.length-1)*TRANSITION_DUR;

// ── Captions loader (async) ───────────────────────────────────────────────────
const ContentCaptionSyncLoader: React.FC<{
  captionsSource: string;
  preset: "bold-center-pop"|"editorial-dimus"|"humane-statement"|"coolvetica-friendly";
  position: "center"|"above-subject"|"bottom-third";
  highlightColor: string;
  emphasisColor: string;
}> = ({captionsSource, preset, position, highlightColor, emphasisColor}) => {
  const [captions, setCaptions] = useState<RemotionCaption[]|null>(null);
  const [handle] = useState(() => delayRender("Loading captions: "+captionsSource));

  useEffect(()=>{
    fetch(staticFile(captionsSource))
      .then(r=>{if(!r.ok)throw new Error(`captions 404: ${r.status}`);return r.json();})
      .then((d:RemotionCaption[])=>{setCaptions(d);continueRender(handle);})
      .catch(e=>{console.error("[ContentReel] captions:",e);cancelRender(e);});
  },[captionsSource,handle]);

  if(!captions) return null;
  return (
    <ContentCaptionSync
      captions={captions}
      preset={preset}
      position={position}
      highlightColor={highlightColor}
      emphasisColor={emphasisColor}
      startFrame={0}
      wordsPerLine={4}
    />
  );
};

// ── ContentReel (main) ────────────────────────────────────────────────────────
export const ContentReel: React.FC<z.infer<typeof contentReelSchema>> = ({
  scenes, bgm, bgmVolume, ttsVolume,
  captionsSource,
  captionsPreset = "bold-center-pop",
  captionsPosition = "center",
  captionsHighlight = "#00DF82",
  captionsEmphasis = "#FECB18",
}) => {
  // Coolvetica font loader
  const [_ch] = useState(()=>delayRender("Coolvetica font"));
  useEffect(()=>{
    const s = document.createElement("style");
    s.textContent = `@font-face{font-family:'Coolvetica';src:url('${staticFile("fonts/Coolvetica-Rg.otf")}') format('opentype');font-weight:400;}`;
    document.head.appendChild(s);
    document.fonts.load("400 40px Coolvetica").then(()=>continueRender(_ch)).catch(()=>continueRender(_ch));
  },[_ch]);

  let acc = 0;
  const timeline = scenes.map((sc,i)=>{
    const from = acc;
    acc += sc.durFrames + (i<scenes.length-1 ? TRANSITION_DUR : 0);
    return {from, dur: sc.durFrames};
  });

  return (
    <AbsoluteFill style={{background:"#0a0a0a"}}>
      {/* BGM */}
      <Audio src={staticFile(bgm)} volume={bgmVolume}/>

      {/* Cenas */}
      {scenes.map((s,i)=>(
        <Sequence key={i} from={timeline[i].from} durationInFrames={timeline[i].dur}>
          <SceneFade duration={timeline[i].dur}>
            <SceneRenderer s={s} ttsVolume={ttsVolume}/>
          </SceneFade>
        </Sequence>
      ))}

      {/* Overlays perpétuos — cima de tudo */}
      <Noise/><Vignette/><FilmGrain/>

      {/* Captions kinetic */}
      {captionsSource && (
        <ContentCaptionSyncLoader
          captionsSource={captionsSource}
          preset={captionsPreset}
          position={captionsPosition}
          highlightColor={captionsHighlight}
          emphasisColor={captionsEmphasis}
        />
      )}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
